import { STALE_TIMES } from '@iheartjane/react-query-utils';
import { useMemo } from 'react';

import { useGetStore, useGoldConfig } from '@jane/shared/data-access';
import { checkLocationAndStoreId } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { BrandSpecial } from '@jane/shared/models';
import type { Store } from '@jane/shared/types';
import type { ProductWithBrandSpecialSegments } from '@jane/shared/util';

import { useKioskUserSegmentIds } from '../../../data-access';
import type { RootLoaderType } from '../../../data-access';
import { useTypedRouteLoaderData } from '../useTypedRouteLoaderData';

export interface ShouldKioskShowGoldProps {
  brandSpecial?: Pick<BrandSpecial, 'rules'>;
  product?: ProductWithBrandSpecialSegments;
}

export const useShouldKioskShowGold = ({
  brandSpecial,
  product,
}: ShouldKioskShowGoldProps = {}): boolean => {
  const { storeId, store: initialStore } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  const { data: goldConfig, isLoading: isLoadingGoldConfig } = useGoldConfig();

  const isAllowed =
    store?.kiosk_allow_auth && store?.kiosk_anonymous_checkout !== 'required';

  const userSegments = useKioskUserSegmentIds({
    enabled: (!!product || !!brandSpecial) && isAllowed,
    staleTime: STALE_TIMES.long,
  });

  return useMemo(() => {
    if (!isAllowed) return false;
    if (isLoadingGoldConfig) return false;

    // check location and store ID and ignore the original janeGold flag
    if (goldConfig) {
      const validState = checkLocationAndStoreId(goldConfig, 'kiosk', store);

      return validState;
    }

    return true;
  }, [
    goldConfig,
    isAllowed,
    isLoadingGoldConfig,
    brandSpecial,
    product,
    store,
    userSegments,
  ]);
};
